import React, { useState } from 'react';
import type { BioData, CityLink, FieldErrors } from '../../types/onboarding';
import { SelectField, TextField } from './OnboardingField';
import { GoldButton, OnboardingProgress, StepHeader, TnsWordmark } from './OnboardingShell';
interface StepBioDataProps {
  value: BioData;
  onChange: (value: BioData) => void;
  cities: CityLink[];
  citiesLoading: boolean;
  onContinue: () => void;
}
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const validateBioData = (bio: BioData): FieldErrors => {
  const errors: FieldErrors = {};
  if (!bio.fullName.trim()) errors.fullName = 'We’d love to know your name.';
  if (!bio.email.trim()) {
    errors.email = 'We need an email to reach you.';
  } else if (!EMAIL_PATTERN.test(bio.email.trim())) {
    errors.email = 'That email doesn’t look quite right.';
  }
  if (!bio.phone.trim()) errors.phone = 'A phone number helps us connect.';
  if (!bio.expertise.trim()) errors.expertise = 'Tell us what you do — a few words is plenty.';
  if (!bio.cityId) errors.cityId = 'Choose the city closest to you.';
  if (bio.cityId === 'OTH' && !bio.otherCityName.trim()) errors.otherCityName = 'Which city are you in?';
  return errors;
};
export const StepBioData = ({
  value,
  onChange,
  cities,
  citiesLoading,
  onContinue
}: StepBioDataProps) => {
  const [errors, setErrors] = useState<FieldErrors>({});
  const set = <K extends keyof BioData,>(key: K, next: BioData[K]) => {
    onChange({
      ...value,
      [key]: next
    });
    if (errors[key as string]) {
      setErrors(prev => {
        const copy = {
          ...prev
        };
        delete copy[key as string];
        return copy;
      });
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const found = validateBioData(value);
    setErrors(found);
    if (Object.keys(found).length === 0) onContinue();
  };
  return <>
      <TnsWordmark className="mb-10 sm:mb-14" data-id="element-914" data-mp="3leuxn08glljy" />
      <OnboardingProgress step={1} data-id="element-915" data-mp="3ldjt2y76w9rs" />
      <StepHeader heading="Let’s start with the basics." subtext="This tells us who you are. The next page tells us why you belong." data-id="element-916" data-mp="3lci8o34aaaxe" />

      <form onSubmit={handleSubmit} noValidate className="space-y-7" data-id="element-917" data-mp="39bsa28wv97ug">
        <TextField id="fullName" label="Full Name" value={value.fullName} onChange={v => set('fullName', v)} error={errors.fullName} autoComplete="name" data-id="element-918" data-mp="3n4hw2dceacrk" />
        <TextField id="email" label="Email" type="email" value={value.email} onChange={v => set('email', v)} error={errors.email} autoComplete="email" data-id="element-919" data-mp="2ug2lc30aqgm1" />
        <TextField id="phone" label="Phone" type="tel" value={value.phone} onChange={v => set('phone', v)} error={errors.phone} autoComplete="tel" data-id="element-920" data-mp="34be2erz6fzwy" />
        <TextField id="brandName" label="Brand Name" optional value={value.brandName} onChange={v => set('brandName', v)} placeholder="If you operate under a brand or company name" data-id="element-921" data-mp="eof034unacj2" />
        <TextField id="expertise" label="Expertise" value={value.expertise} onChange={v => set('expertise', v)} error={errors.expertise} placeholder="e.g. Brand strategy, ESG consulting, product design" data-id="element-922" data-mp="21mfsm38pf24c" />
        <SelectField id="cityId" label="City" value={value.cityId} onChange={v => set('cityId', v)} error={errors.cityId} disabled={citiesLoading} placeholder={citiesLoading ? 'Loading cities…' : 'Select your city'} options={cities.map(c => ({
        value: c.city_id,
        label: c.city_name
      }))} data-id="element-923" data-mp="2gaty6lxi94ol" />
        {value.cityId === 'OTH' && <TextField id="otherCityName" label="Which city are you in?" value={value.otherCityName} onChange={v => set('otherCityName', v)} error={errors.otherCityName} placeholder="Tell us where you’re based" data-id="element-924" data-mp="33zd4ivwzv7e1" />}

        <div className="pt-4" data-id="element-925" data-mp="16cuhgos9fj3h">
          <GoldButton type="submit" data-id="element-926" data-mp="16c2lvdynxld7">Continue</GoldButton>
        </div>
      </form>
    </>;
};